import { useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { clsx } from "clsx";
import { TempleMark, Wordmark } from "@/components/ui/TempleMark";
import { useBackCloseable } from "@/lib/modal-stack";

const slides = [
  { title: "Welcome to Darshan", body: "Manage your property, bookings and guests in one place." },
  { title: "Never miss a booking", body: "Get instant alerts for new requests and confirm them before they expire." },
  { title: "Run your stay with ease", body: "Check guests in and out, track rooms, payments and reports." },
];

const SWIPE_PX = 50;

// Mobile/app-only intro carousel shown before Login to signed-out users. On wide screens the
// same content lives in Login's left panel instead (see LoginPage).
export function WelcomePage({ onDone }: { onDone: () => void }) {
  const [index, setIndex] = useState(0);
  const startX = useRef<number | null>(null);
  const isLast = index === slides.length - 1;

  const next = () => (isLast ? onDone() : setIndex(index + 1));
  const prev = () => setIndex((current) => Math.max(0, current - 1));

  // Android back steps back through slides; on the first slide BackButtonProvider exits the app.
  useBackCloseable(index > 0, prev);

  const onPointerDown = (event: PointerEvent) => { startX.current = event.clientX; };
  const onPointerUp = (event: PointerEvent) => {
    if (startX.current === null) return;
    const dx = event.clientX - startX.current;
    startX.current = null;
    if (dx <= -SWIPE_PX) next();
    else if (dx >= SWIPE_PX) prev();
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "ArrowRight") next();
    if (event.key === "ArrowLeft") prev();
  };

  return (
    <main className="welcome" aria-roledescription="carousel" aria-label="Welcome to Darshan" onKeyDown={onKeyDown}>
      <header className="welcome__top">
        <Wordmark className="welcome__wordmark" />
        {!isLast && <button type="button" className="welcome__skip" onClick={onDone}>Skip</button>}
      </header>

      <div className="welcome__stage" onPointerDown={onPointerDown} onPointerUp={onPointerUp} onPointerCancel={() => { startX.current = null; }}>
        {index === 0 && (
          <div className="welcome-art welcome-art--mark">
            <span><TempleMark size={112} label="Darshan temple mark" /></span>
          </div>
        )}
        {index === 1 && (
          <div className="welcome-art welcome-art--phone" aria-hidden="true">
            <i className="welcome-art__notch" />
            <div className="welcome-art__card">
              <div><b>NEW REQUEST</b><strong>04:52</strong></div>
              <span>Deluxe · 2 rooms</span>
              <small>14 → 16 Oct · 2 nights</small>
              <em>Confirm</em>
            </div>
            <i className="welcome-art__progress"><i /></i>
          </div>
        )}
        {index === 2 && (
          <div className="welcome-art welcome-art--grid" aria-hidden="true">
            {/* f = free, o = occupied, r = reserved, m = maintenance */}
            {"fofrfooffrffofmofrfo".split("").map((tone, cell) => <i key={cell} className={`welcome-cell welcome-cell--${tone}`} />)}
          </div>
        )}
      </div>

      <section className="welcome__copy" aria-live="polite" aria-roledescription="slide" aria-label={`${index + 1} of ${slides.length}`}>
        <small>{index + 1} of {slides.length}</small>
        <h1>{slides[index].title}</h1>
        <p>{slides[index].body}</p>
      </section>

      <div className="welcome__dots">
        {slides.map((slide, dot) => (
          <button key={slide.title} type="button" aria-label={`Go to slide ${dot + 1} of ${slides.length}`} aria-current={dot === index ? "step" : undefined} onClick={() => setIndex(dot)}>
            <span className={clsx(dot === index && "active")} />
          </button>
        ))}
      </div>

      <button type="button" className="welcome__cta" onClick={next}>{isLast ? "Get started" : "Next"}</button>
    </main>
  );
}
